import {LETTERS} from '../constants';

function getRandLetter() {
    const randIndex = Math.floor(Math.random() * LETTERS.length);
    return LETTERS[randIndex];
}

export const HORIZONTAL = 'horizontal';
export const VERTICAL = 'vertical';
export const BACK_DIAGONAL = 'backDiagonal';
export const FORWARD_DIAGONAL = 'forwardDiagonal';

const DIR_DICTIONARY = {
    '-1,0': HORIZONTAL,
    '1,0': HORIZONTAL,
    '0,-1': VERTICAL,
    '0,1': VERTICAL,
    '-1,-1': BACK_DIAGONAL,
    '1,1': BACK_DIAGONAL,
    '1,-1': FORWARD_DIAGONAL,
    '-1,1': FORWARD_DIAGONAL,
    '0,0': null,
};

let rowOffset;
let colOffset;
let rowCount;
let colCount;

/**
 *
 * @param {array} words An array of word objects
 */
export function getWordsState(words) {
    rowOffset = 0;
    colOffset = 0;
    rowCount = 0;
    colCount = 0;

    const lettersPositions = getLettersPositions(words);

    const rows = rowCount - rowOffset + 1;
    const cols = colCount - colOffset + 1;

    return {letters: getLettersState(lettersPositions), rows, cols};
}

export function getLettersPositions(words) {
    return words.reduce((prev, curr) => {
        return addWordLettersPositions(curr.text, prev);
    }, {});
}

function addWordLettersPositions(word, prevPositionedLetters = {}) {
    // Create a clone of the positionedLetters object
    const positionedLettersClone = {positionsToLetter: {}, lettersToPositions: {}, ...prevPositionedLetters};
    let positionedWord;

    // If there are no positioned letters already
    if (!Object.keys(prevPositionedLetters).length) {
        return addFirstWordLettersPositions(word, positionedLettersClone);
    } else {
        positionedWord = getWordLettersPositions(word, positionedLettersClone);
    }

    // If we couldn't fit the word in either vertically or horizontally
    // let's pick a random empty position and try again
    if (!positionedWord) {
        const randomEmptyPosition = getRandomEmptyPosition(positionedLettersClone.positionsToLetter);

        // Set the position for the first letter of the word
        // and try again to position the whole thing (now that we have a letter entry)
        addPosition(positionedLettersClone, word.charAt(0), randomEmptyPosition);
        return addWordLettersPositions(word, positionedLettersClone);
    }

    return positionedLettersClone;
}

function addFirstWordLettersPositions(word, lettersPositions) {
    const x = Math.floor(Math.random() * 10);
    const y = Math.floor(Math.random() * 10);

    for (let i = 0; i < word.length; i++) {
        const letter = word.charAt(i);
        const position = `${x}_${y + i}`;
        addPosition(lettersPositions, letter, position);
    }

    return lettersPositions;
}

function getWordLettersPositions(word, lettersPositions) {
    let positionedWord;

    for (let i = 0; i < word.length; i++) {
        const letter = word.charAt(i);

        // We would like to attempt crossing another word potentially 50% of the times
        const shouldAttemptCrossingAnotherWord = Math.random() < 0.5;
        let foundLetterEntry;
        if (shouldAttemptCrossingAnotherWord) {
            foundLetterEntry = getRandomEmptyPosition(lettersPositions.positionsToLetter);
        } else {
            const letterPositions = lettersPositions.lettersToPositions[letter];
            if (letterPositions) {
                const randomPositionIndex = Math.floor(Math.random * (letterPositions.length - 1));
                foundLetterEntry = letterPositions[randomPositionIndex];
            }
            foundLetterEntry =
                lettersPositions.lettersToPositions[letter] && lettersPositions.lettersToPositions[letter][0];
        }

        // If we found a valid position lets see if the word can be
        // fitted vertically or horizontally
        if (foundLetterEntry) {
            positionedWord = getVerticallyPositionedWord(word, i, foundLetterEntry, lettersPositions);
            if (positionedWord) {
                // Update the row offset if needed
                const rowOffsetCandidate = foundLetterEntry.split('_')[0] - i;
                rowOffset = rowOffsetCandidate < rowOffset ? rowOffsetCandidate : rowOffset;
                break;
            }
            positionedWord = getHorizontallyPositionedWord(word, i, foundLetterEntry, lettersPositions);
            if (positionedWord) {
                // Update the col offset if needed
                const colOffsetCandidate = foundLetterEntry.split('_')[1] - i;
                colOffset = colOffsetCandidate < colOffset ? colOffsetCandidate : colOffset;
                break;
            }
        }
    }

    // If we could fit the word in either vertically or horizontally
    // let's add the the new letter positions to the entire letters
    // positions objects
    if (positionedWord) {
        lettersPositions.positionsToLetter = {
            ...lettersPositions.positionsToLetter,
            ...positionedWord.positionsToLetter,
        };
        for (const key in positionedWord.lettersToPositions) {
            if (positionedWord.lettersToPositions.hasOwnProperty(key)) {
                const letterPositions = positionedWord.lettersToPositions[key];
                if (lettersPositions.lettersToPositions[key]) {
                    // Produce an array with unique values
                    const uniqueConcatenation = new Set(
                        lettersPositions.lettersToPositions[key].concat(letterPositions)
                    );
                    lettersPositions.lettersToPositions[key] = Array.from(uniqueConcatenation);
                } else {
                    lettersPositions.lettersToPositions[key] = letterPositions;
                }
            }
        }
    }

    return positionedWord;
}

function getRandomEmptyPosition(positionsToLetter) {
    let position;
    do {
        const randomRow = Math.floor(Math.random() * 10);
        const randomCol = Math.floor(Math.random() * 10);
        position = `${randomRow}_${randomCol}`;
    } while (positionsToLetter[position]);
    return position;
}

function addPosition(positionedLetters, letter, newPosition) {
    positionedLetters.positionsToLetter[newPosition] = letter;
    const currentLetterPositions = positionedLetters.lettersToPositions[letter] || [];
    positionedLetters.lettersToPositions[letter] = [...currentLetterPositions, newPosition];

    // Set the row and columns max for later calculations for the board size
    const position = newPosition.split('_');
    const row = parseInt(position[0], 10);
    const col = parseInt(position[1], 10);
    if (row > rowCount) rowCount = row;
    if (col > colCount) colCount = col;
}

function getVerticallyPositionedWord(word, i, foundLetterEntry, positionedLetters) {
    const location = foundLetterEntry.split('_');
    const rowIndex = parseInt(location[0], 10);
    const colIndex = parseInt(location[1], 10);
    // Trace back to the starting row of the word
    // Say we found the 3rd letter, we would like to start the check
    // from the first letter
    const startRowIndex = rowIndex - i;
    const tempLettersPositions = {
        positionsToLetter: {},
        lettersToPositions: {},
    };

    for (let j = 0; j < word.length; j++) {
        const letter = word.charAt(j);
        const currentRowIndex = startRowIndex + j;
        const letterOnThisPosition = positionedLetters.positionsToLetter[`${currentRowIndex}_${colIndex}`];
        if (letterOnThisPosition && letterOnThisPosition !== letter) return false;
        addPosition(tempLettersPositions, letter, `${currentRowIndex}_${colIndex}`);
    }

    return tempLettersPositions;
}

function getHorizontallyPositionedWord(word, i, foundLetterEntry, positionedLetters) {
    const location = foundLetterEntry.split('_');
    const rowIndex = parseInt(location[0], 10);
    const colIndex = parseInt(location[1], 10);
    const startColIndex = colIndex - i;
    const temp = {
        positionsToLetter: {},
        lettersToPositions: {},
    };

    for (let j = 0; j < word.length; j++) {
        const letter = word.charAt(j);
        const currentColIndex = startColIndex + j;
        const letterOnThisPosition = positionedLetters.positionsToLetter[`${rowIndex}_${currentColIndex}`];
        if (letterOnThisPosition && letterOnThisPosition !== letter) return false;

        temp[letter] = temp[letter] || [];
        addPosition(temp, letter, `${rowIndex}_${currentColIndex}`);
    }

    return temp;
}

function getLettersState(lettersPositions) {
    let lettersFlatArr = [];
    if (lettersPositions && lettersPositions.positionsToLetter) {
        for (let i = rowOffset; i <= rowCount; i++) {
            for (let j = colOffset; j <= colCount; j++) {
                const letter = lettersPositions.positionsToLetter[`${i}_${j}`] || getRandLetter();
                lettersFlatArr.push({
                    value: letter,
                    id: `${letter}_${i}_${j}`,
                    xPos: j - colOffset,
                    yPos: i - rowOffset,
                    used: false,
                });
            }
        }
    }

    return lettersFlatArr;
}

export function getDirectionByCoordinates(firstCoordinate, secondCoordinate) {
    if (arguments.length < 2) {
        throw Error('Missing arguments');
    }

    if (
        isNaN(firstCoordinate.xPos) ||
        isNaN(firstCoordinate.yPos) ||
        isNaN(secondCoordinate.xPos) ||
        isNaN(secondCoordinate.yPos)
    ) {
        throw Error('Arguments are not coordinates');
    }

    const resultX = firstCoordinate.xPos - secondCoordinate.xPos;
    const resultY = firstCoordinate.yPos - secondCoordinate.yPos;
    const key = `${resultX},${resultY}`;

    return DIR_DICTIONARY[key];
}
